import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Sky, Stars } from '@react-three/drei';
import { Physics, RigidBody } from '@react-three/rapier';
import Stage from './Stage';
import CityDecorations from './CityDecorations';
import SkyBox from './SkyBox';
import Player from '../player/Player';
import { audioManager } from '../audio/AudioManager';
import { useGameStore } from '../store/gameStore';
import { terrainHeight, displaceTerrainGeometry } from './Terrain';
import type { BuildingCollider } from '../player/CollisionDetection';
import type { CityData } from '../types/city';

/**
 * Physics crates placed near each stage — pushable by the player.
 */
function PhysicsCrates({ cityData }: { cityData: CityData }) {
  const crates: Array<{ x: number; z: number; y: number }> = [];
  cityData.districts.forEach((d) => {
    const cols = Math.ceil(Math.sqrt(d.tracks.length));
    d.tracks.forEach((_, i) => {
      const row = Math.floor(i / cols);
      const col = i % cols;
      const xRange = d.bounds.x[1] - d.bounds.x[0];
      const zRange = d.bounds.z[1] - d.bounds.z[0];
      const cellW = xRange / cols;
      const cellD = zRange / Math.ceil(d.tracks.length / cols);
      const sx = d.bounds.x[0] + col * cellW + cellW / 2;
      const sz = d.bounds.z[0] + row * cellD + cellD / 2;
      const sy = terrainHeight(sx, sz);
      crates.push({ x: sx + 3, z: sz + 2, y: sy + 0.25 });
      crates.push({ x: sx - 3, z: sz + 2, y: sy + 0.25 });
    });
  });

  return (
    <>
      {crates.map((c, i) => (
        <RigidBody key={`crate-${i}`} position={[c.x, c.y, c.z]} colliders="cuboid">
          <mesh castShadow>
            <boxGeometry args={[0.5, 0.5, 0.5]} />
            <meshStandardMaterial color="#8B4513" roughness={0.7} />
          </mesh>
        </RigidBody>
      ))}
    </>
  );
}

interface CitySceneProps {
  cityData: CityData;
}

/** Seeded RNG matching CityDecorations for tree collider generation */
function createSeededRng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

/** Generate tree collider positions (must match CityDecorations tree scatter) */
function generateTreeColliders(): BuildingCollider[] {
  const colliders: BuildingCollider[] = [];
  const rng = createSeededRng(42);
  for (let i = 0; i < 40; i++) {
    const x = -38 + rng() * 76;
    const z = -22 + rng() * 44;
    colliders.push({ position: [x, 0, z], size: [0.5, 3, 0.5] });
  }
  return colliders;
}

/** Bench colliders at known positions — Y sampled from terrain */
function getBenchColliders(): BuildingCollider[] {
  return [-8, -3, 3, 8].map((x, i) => {
    const z = i % 2 === 0 ? 1.8 : -1.8;
    return {
      position: [x, terrainHeight(x, z), z] as [number, number, number],
      size: [1.5, 0.5, 0.6] as [number, number, number],
    };
  });
}

/** Lamppost collider positions (match CityDecorations StreetLamp scatter) */
function getLampColliders(): BuildingCollider[] {
  return [-22, -14, -6, 0, 8, 14, 22].map((x, i) => {
    const z = i % 2 === 0 ? 2 : -2;
    return {
      position: [x, terrainHeight(x, z), z] as [number, number, number],
      size: [0.3, 3.7, 0.3] as [number, number, number],
    };
  });
}

/** Crate colliders matching PhysicsCrates positions */
function getCrateColliders(cityData: CityData): BuildingCollider[] {
  const colliders: BuildingCollider[] = [];
  cityData.districts.forEach((d) => {
    const cols = Math.ceil(Math.sqrt(d.tracks.length));
    d.tracks.forEach((_, i) => {
      const row = Math.floor(i / cols);
      const col = i % cols;
      const xRange = d.bounds.x[1] - d.bounds.x[0];
      const zRange = d.bounds.z[1] - d.bounds.z[0];
      const cellW = xRange / cols;
      const cellD = zRange / Math.ceil(d.tracks.length / cols);
      const sx = d.bounds.x[0] + col * cellW + cellW / 2;
      const sz = d.bounds.z[0] + row * cellD + cellD / 2;
      const sy = terrainHeight(sx, sz);
      colliders.push(
        { position: [sx + 3, sy + 0.25, sz + 2], size: [0.5, 0.5, 0.5] },
        { position: [sx - 3, sy + 0.25, sz + 2], size: [0.5, 0.5, 0.5] },
      );
    });
  });
  return colliders;
}

/**
 * Terrain ground plane with height displacement.
 * Clickable for movement — uses R3F's built-in intersection.
 */
function TerrainGround({ onClick }: { onClick: (point: THREE.Vector3) => void }) {
  const geoRef = useRef<THREE.PlaneGeometry>(null);

  useEffect(() => {
    if (geoRef.current) {
      displaceTerrainGeometry(geoRef.current);
    }
  }, []);

  const handleClick = useCallback((e: any) => {
    if (e.point) {
      onClick(e.point.clone());
    }
  }, [onClick]);

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.01, 0]}
      receiveShadow
      onClick={handleClick}
    >
      <planeGeometry ref={geoRef} args={[90, 90, 64, 64]} />
      <meshStandardMaterial color="#7aaf6d" roughness={0.85} />
    </mesh>
  );
}

/**
 * Spotlight that shines down on the currently-playing stage.
 */
function PlayingStageSpotlight({ cityData }: { cityData: CityData }) {
  const activeTrackId = useGameStore((s) => s.activeTrackId);
  const lockedTrackId = useGameStore((s) => s.lockedTrackId);
  const trackId = lockedTrackId || activeTrackId;
  if (!trackId) return null;

  const stagePos = cityData.districts.flatMap((d) =>
    d.tracks.map((t, i) => {
      const cols = Math.ceil(Math.sqrt(d.tracks.length));
      const row = Math.floor(i / cols);
      const col = i % cols;
      const xRange = d.bounds.x[1] - d.bounds.x[0];
      const zRange = d.bounds.z[1] - d.bounds.z[0];
      const cellW = xRange / cols;
      const cellD = zRange / Math.ceil(d.tracks.length / cols);
      return {
        id: t.id,
        x: d.bounds.x[0] + col * cellW + cellW / 2,
        z: d.bounds.z[0] + row * cellD + cellD / 2,
      };
    }),
  ).find((s) => s.id === trackId);

  if (!stagePos) return null;

  const ty = terrainHeight(stagePos.x, stagePos.z);

  return (
    <spotLight
      position={[stagePos.x, ty + 15, stagePos.z]}
      angle={0.5}
      penumbra={0.5}
      intensity={3}
      color="#ffdd88"
      distance={25}
      castShadow
      target-position={[stagePos.x, ty, stagePos.z]}
    />
  );
}

/**
 * Main 3D festival scene with hilly terrain, stages, trees, player.
 */
export default function CityScene({ cityData }: CitySceneProps) {
  const stagePositionsRef = useRef<Array<{ trackId: string; position: [number, number, number] }>>([]);
  const moveTargetRef = useRef<THREE.Vector3 | null>(null);
  const markerRef = useRef<THREE.Mesh>(null);
  const { camera, gl } = useThree();

  // Generate all static colliders: trees, benches, lamps, crates
  const staticColliders = useMemo(() => {
    return [
      ...generateTreeColliders(),
      ...getBenchColliders(),
      ...getLampColliders(),
      ...getCrateColliders(cityData),
    ];
  }, [cityData]);

  // State for all colliders — starts with trees/benches, stages append as they mount
  const [buildingColliders, setBuildingColliders] = useState<BuildingCollider[]>(staticColliders);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => { audioManager.stop(); };
  }, []);

  // Register stage positions (for audio proximity + collision)
  const handlePositionRegistered = useCallback(
    (trackId: string, position: [number, number, number], size: [number, number, number]) => {
      const existing = stagePositionsRef.current.find((b) => b.trackId === trackId);
      if (!existing) {
        stagePositionsRef.current.push({ trackId, position });
        setBuildingColliders((prev) => [...prev, { position, size }]);
        audioManager.registerBuildings(stagePositionsRef.current);
      }
    },
    [],
  );

  // Click-to-move: stores the terrain intersection point
  const handleTerrainClick = useCallback((point: THREE.Vector3) => {
    moveTargetRef.current = point;
    if (markerRef.current) {
      markerRef.current.position.copy(point);
      markerRef.current.position.y += 0.1;
      markerRef.current.visible = true;
    }
  }, []);

  // Build stages from districts
  const stages = cityData.districts.flatMap((district) => {
    const { bounds } = district;
    const trackCount = district.tracks.length;

    return district.tracks.map((track, i) => {
      const cols = Math.ceil(Math.sqrt(trackCount));
      const row = Math.floor(i / cols);
      const col = i % cols;

      const xRange = bounds.x[1] - bounds.x[0];
      const zRange = bounds.z[1] - bounds.z[0];
      const cellW = xRange / cols;
      const cellD = zRange / Math.ceil(trackCount / cols);

      const x = bounds.x[0] + col * cellW + cellW / 2;
      const z = bounds.z[0] + row * cellD + cellD / 2;
      // Sample all 4 corners + center so stages don't clip into hills
      const ty = Math.max(
        terrainHeight(x, z),
        terrainHeight(x - 3.5, z - 2.5),
        terrainHeight(x + 3.5, z - 2.5),
        terrainHeight(x - 3.5, z + 2.5),
        terrainHeight(x + 3.5, z + 2.5),
      );

      return (
        <Stage
          key={track.id}
          track={track}
          position={[x, ty, z]}
          districtColor={district.color}
          onPositionRegistered={handlePositionRegistered}
        />
      );
    });
  });

  return (
    <>
      {/* === SUNSET ENVIRONMENT === */}
      <Sky
        sunPosition={[80, 2, -60]}
        turbidity={8}
        rayleigh={0.5}
        mieCoefficient={0.005}
        mieDirectionalG={0.7}
      />
      {/* Starfield */}
      <Stars
        radius={50}
        depth={30}
        count={800}
        factor={4}
        saturation={0}
        fade
        speed={0.5}
      />
      {/* Skybox: distant mountains + cloud layers */}
      <SkyBox />
      <ambientLight intensity={0.3} color="#ffccaa" />
      <directionalLight
        position={[30, 8, -20]}
        intensity={0.8}
        color="#ff9966"
        castShadow
        shadow-mapSize-width={512}
        shadow-mapSize-height={512}
        shadow-camera-far={60}
        shadow-camera-left={-20}
        shadow-camera-right={20}
        shadow-camera-top={20}
        shadow-camera-bottom={-20}
      />
      <directionalLight
        position={[-20, 5, 30]}
        intensity={0.15}
        color="#8899cc"
      />

      {/* Hilly terrain ground — click for movement */}
      <TerrainGround onClick={handleTerrainClick} />

      {/* Decorations */}
      <CityDecorations cityData={cityData} />

      {/* Click marker */}
      <mesh ref={markerRef} rotation={[-Math.PI / 2, 0, 0]} visible={false}>
        <ringGeometry args={[0.3, 0.45, 32]} />
        <meshBasicMaterial color="#FFD700" side={THREE.DoubleSide} transparent opacity={0.8} />
      </mesh>

      {/* Physics world — stages, crates, player all interact */}
      <Physics gravity={[0, -9.81, 0]}>
        {/* Stages */}
        {stages}

        {/* Pushable crates near each stage */}
        <PhysicsCrates cityData={cityData} />

        {/* Spotlight on playing stage */}
        <PlayingStageSpotlight cityData={cityData} />

        {/* Player */}
        <Player
          spawnPoint={cityData.spawnPoint}
          buildingPositions={buildingColliders}
          moveTargetRef={moveTargetRef}
          onMoveStart={() => {
            if (markerRef.current) markerRef.current.visible = false;
          }}
        />
      </Physics>
    </>
  );
}
